import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { useGameStatusStore } from '../../store/game-status-store';
import {
  getTimeUntilNoOneJoinedStatus,
  shouldTriggerShowNoOneJoin,
} from '../../utils/init-game-statuses';
import { mainnet } from 'viem/chains';

import { CardPlayerEnum } from 'modules/Room/constants/enums';
import { GameStatusEnum } from 'modules/Room/game-status/constants/game-status-enum';

import { NULL_ADDRESS } from 'shared/constants/constants';
import { RoomStatusEnum } from 'shared/constants/shared-enums';
import { NO_PLAYER_FOUND_TIMEOUT } from 'shared/constants/timers';
import { Timer } from 'shared/constants/types';
import { useAccount } from 'shared/hooks/use-web3';
import { useRoomStore } from 'shared/store/roomStore';

import { JoinGameStatus, NoOneJoinStatus } from './statuses';

interface InitGameStatusesProps {
  player?: CardPlayerEnum;
}

export const InitGameStatuses = ({ player }: InitGameStatusesProps) => {
  const timerRef = useRef<Timer | null>(null);
  const { roomStatus, playerB, playerA, closerRoom } = useRoomStore();
  const { activeRoomId } = useParams();
  const { address, chainId } = useAccount();
  const { resetGameStatus, gameStatus, setGameStatus } = useGameStatusStore();

  useEffect(() => {
    if (
      shouldTriggerShowNoOneJoin({
        activeRoomId,
        roomStatus,
        playerB,
        playerA,
        address,
        closerRoom,
      })
    ) {
      let diff = getTimeUntilNoOneJoinedStatus(closerRoom) + NO_PLAYER_FOUND_TIMEOUT; // 90 seconds
      if (chainId === mainnet.id) {
        timerRef.current = setTimeout(() => {
          setGameStatus(GameStatusEnum.NO_ONE_JOIN);
        }, NO_PLAYER_FOUND_TIMEOUT);
      } else if (diff > 0) {
        resetGameStatus();
        timerRef.current = setTimeout(() => {
          setGameStatus(GameStatusEnum.NO_ONE_JOIN);
        }, diff);
      } else {
        timerRef.current = null;
        resetGameStatus();
        setGameStatus(GameStatusEnum.NO_ONE_JOIN);
      }
    }
    return () => clearTimeout(timerRef.current as Timer);
  }, [
    activeRoomId,
    roomStatus,
    playerB,
    playerA,
    address,
    closerRoom,
    resetGameStatus,
    setGameStatus,
  ]);

  // status to take your bet back
  if (gameStatus === GameStatusEnum.NO_ONE_JOIN && (player === CardPlayerEnum.Player || !player)) {
    return (
      <NoOneJoinStatus
        isInvited={playerB.address !== NULL_ADDRESS && roomStatus === RoomStatusEnum.Open}
      />
    );
  }

  // this status shown if playerB is not connected
  if (
    (playerB.address === NULL_ADDRESS || playerB.address === address?.toLowerCase()) &&
    playerA.address !== address?.toLowerCase() &&
    roomStatus === RoomStatusEnum.Open &&
    (player === CardPlayerEnum.Connect || !player || player === CardPlayerEnum.Player)
  ) {
    return <JoinGameStatus />;
  }

  return null;
};
